import { motion } from "framer-motion";
import { Users, Star, ArrowRight, Lock, Mic, IndianRupee, Sparkles, ChevronRight } from "lucide-react";

const CREATORS = [
  {
    id: 1,
    name: "RohitFanatic",
    handle: "@rohitfanatic",
    specialty: "Cricket Analysis",
    followers: "2.3M",
    posts: "1,240",
    rating: 4.9,
    gradient: "from-[#3B9EFF]/20 to-[#3B9EFF]/5",
    accent: "#3B9EFF",
    image: "https://images.unsplash.com/photo-1518809595274-1471d16319b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtYW4lMjBmYWNlJTIwcG9ydHJhaXQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzQ1MzQzNTl8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
    tags: ["Test Cricket", "IPL", "Stats"],
  },
  {
    id: 2,
    name: "GoalKingFC",
    handle: "@goalkingfc",
    specialty: "Football Commentary",
    followers: "1.8M",
    posts: "980",
    rating: 4.8,
    gradient: "from-blue-500/20 to-blue-500/5",
    accent: "#007AFF",
    image: "https://images.unsplash.com/photo-1712153483492-cab99d90ca71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtYW4lMjBmYWNlJTIwcG9ydHJhaXQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzQ1MzQzNTl8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-7",
    tags: ["Premier League", "UCL", "Transfers"],
  },
  {
    id: 3,
    name: "CricketVault",
    handle: "@cricketvault",
    specialty: "Cricket History & Stats",
    followers: "4.1M",
    posts: "3,600",
    rating: 5.0,
    gradient: "from-orange-500/20 to-orange-500/5",
    accent: "#f59e0b",
    image: null,
    span: "md:col-span-4",
    tags: ["Records", "History", "Analysis"],
  },
  {
    id: 4,
    name: "SportsDesk24",
    handle: "@sportsdesk24",
    specialty: "Breaking Sports News",
    followers: "3.2M",
    posts: "8,900",
    rating: 4.7,
    gradient: "from-red-500/20 to-red-500/5",
    accent: "#ef4444",
    image: null,
    span: "md:col-span-4",
    tags: ["News", "Transfers", "Live"],
  },
  {
    id: 5,
    name: "IPLKingdom",
    handle: "@iplkingdom",
    specialty: "IPL Expert",
    followers: "900K",
    posts: "520",
    rating: 4.6,
    gradient: "from-purple-500/20 to-purple-500/5",
    accent: "#a855f7",
    image: null,
    span: "md:col-span-4",
    tags: ["IPL", "Auction", "Fantasy"],
  },
];

function CreatorCard({ creator, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay }}
      data-testid={`creator-card-${creator.id}`}
      className={`${creator.span} bg-[#171B24] border border-white/5 hover-glow rounded-2xl overflow-hidden group hover:-translate-y-2 transition-transform duration-150`}
    >
      {creator.image ? (
        /* Large card with image */
        <div className="flex flex-col sm:flex-row h-full">
          <div className="sm:w-48 h-48 sm:h-auto relative overflow-hidden flex-shrink-0">
            <img
              src={creator.image}
              alt={creator.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#171B24]/60" />
          </div>
          <div className="p-6 flex flex-col justify-between flex-1">
            <div>
              <span
                className="text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full mb-3 inline-block"
                style={{ background: `${creator.accent}20`, color: creator.accent, border: `1px solid ${creator.accent}40` }}
              >
                {creator.specialty}
              </span>
              <h3 className="font-outfit font-bold text-white text-xl mb-1">{creator.name}</h3>
              <p className="text-[#A1A1AA] text-sm">{creator.handle}</p>
            </div>
            <div>
              <div className="flex gap-1.5 flex-wrap mb-4 mt-3">
                {creator.tags.map((tag) => (
                  <span key={tag} className="text-[10px] text-[#A1A1AA] bg-white/5 px-2 py-1 rounded-full">{tag}</span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                  <Users size={14} />
                  <span className="font-semibold text-white">{creator.followers}</span>
                  <span className="text-xs">followers</span>
                </div>
                <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                  <Star size={12} className="fill-[#3B9EFF] text-[#3B9EFF]" />
                  <span className="text-white font-semibold text-sm">{creator.rating}</span>
                </div>
              </div>
              <a
                href="#download"
                data-testid={`creator-follow-btn-${creator.id}`}
                className="mt-4 w-full flex items-center justify-center gap-1.5 border text-xs font-bold uppercase tracking-wider rounded-full py-2.5 transition-all duration-100"
                style={{ borderColor: `${creator.accent}50`, color: creator.accent }}
              >
                Follow in App
              </a>
            </div>
          </div>
        </div>
      ) : (
        /* Compact card */
        <div className="p-6">
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${creator.gradient} border border-white/5 flex items-center justify-center mb-4`}>
            <span className="text-2xl font-outfit font-black" style={{ color: creator.accent }}>
              {creator.name[0]}
            </span>
          </div>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full mb-3 inline-block"
            style={{ background: `${creator.accent}20`, color: creator.accent, border: `1px solid ${creator.accent}40` }}
          >
            {creator.specialty}
          </span>
          <h3 className="font-outfit font-bold text-white text-lg mb-1">{creator.name}</h3>
          <p className="text-[#A1A1AA] text-xs mb-4">{creator.handle}</p>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
              <Users size={13} />
              <span className="font-bold text-white text-sm">{creator.followers}</span>
            </div>
            <div className="flex items-center gap-1">
              <Star size={11} className="fill-[#3B9EFF] text-[#3B9EFF]" />
              <span className="text-white text-sm font-semibold">{creator.rating}</span>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap mb-4">
            {creator.tags.map((tag) => (
              <span key={tag} className="text-[10px] text-[#A1A1AA] bg-white/5 px-2 py-1 rounded-full">{tag}</span>
            ))}
          </div>
          <a
            href="#download"
            data-testid={`creator-follow-btn-${creator.id}`}
            className="w-full flex items-center justify-center gap-1.5 border text-xs font-bold uppercase tracking-wider rounded-full py-2.5 transition-all duration-300"
            style={{ borderColor: `${creator.accent}50`, color: creator.accent }}
          >
            Follow in App
          </a>
        </div>
      )}
    </motion.div>
  );
}

export default function CreatorsSection() {
  return (
    <section id="creators" data-testid="creators-section" className="py-24 lg:py-32 bg-[#0C0E13]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#3B9EFF]" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#3B9EFF] font-outfit">Top Creators</span>
            <span className="flex items-center gap-1.5 bg-[#3B9EFF]/10 border border-[#3B9EFF]/30 rounded-full px-2.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3B9EFF]" />
              <span className="text-[9px] font-black uppercase tracking-wider text-[#3B9EFF]">At Launch</span>
            </span>
          </div>
          <h2 className="font-outfit font-bold text-4xl sm:text-5xl tracking-tight text-white mb-4">
            Follow the<br />
            <span className="text-[#3B9EFF]">Voices You Love</span>
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] max-w-xl font-inter">
            Cricket analysts, football pundits, and sports journalists — discover creators who know the game as well as you do.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {CREATORS.map((creator, i) => (
            <CreatorCard key={creator.id} creator={creator} delay={i * 0.1} />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <a
            href="#download"
            data-testid="creators-see-all-btn"
            className="inline-flex items-center gap-2 border border-white/20 text-white font-bold rounded-full px-8 py-3.5 hover:border-[#3B9EFF]/50 hover:text-[#3B9EFF] transition-all duration-300 text-sm"
          >
            Discover All Creators
            <ArrowRight size={16} />
          </a>
        </motion.div>

        {/* Shoutout Feature — Phase 2 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-16 relative rounded-3xl overflow-hidden"
          style={{ border: "1px solid rgba(255,255,255,0.06)", background: "linear-gradient(135deg, #0f1220 0%, #0C0E13 60%)" }}
        >
          {/* Glow accent */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-[#a855f7]/8 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-[#3B9EFF]/6 rounded-full blur-[80px] pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">
            {/* Left: Feature info */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-[#a855f7]/15 border border-[#a855f7]/25 flex items-center justify-center flex-shrink-0">
                  <Mic size={20} className="text-[#a855f7]" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-outfit font-black text-white text-lg tracking-tight">Shoutout</span>
                    <span className="flex items-center gap-1 bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-full px-2 py-0.5">
                      <Lock size={8} className="text-[#f59e0b]" />
                      <span className="text-[8px] font-black uppercase tracking-widest text-[#f59e0b]">Coming Soon</span>
                    </span>
                  </div>
                  <p className="text-[#A1A1AA] text-xs mt-0.5">Fan-to-Creator payments</p>
                </div>
              </div>

              <h3 className="font-outfit font-black text-white text-2xl sm:text-3xl leading-tight tracking-tight mb-4">
                Send a Shoutout to Your<br />
                <span className="text-[#a855f7]">Favourite Creator</span>
              </h3>

              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-8 max-w-md font-inter">
                Request a personalised message from any creator on Fanible — birthday dedications, match predictions, fan shoutouts. You pay, they record. Simple, direct, and powerful.
              </p>

              {/* How it works */}
              <div className="space-y-3 mb-8">
                {[
                  { step: "01", text: "Pick a creator and choose your shoutout type", icon: Users },
                  { step: "02", text: "Pay securely using Fanible Coins or UPI", icon: IndianRupee },
                  { step: "03", text: "Receive your personalised video within 48 hrs", icon: Sparkles },
                ].map(({ step, text, icon: Icon }) => (
                  <div key={step} className="flex items-center gap-3">
                    <span className="text-[#a855f7] font-outfit font-black text-xs w-6 flex-shrink-0">{step}</span>
                    <Icon size={13} className="text-[#A1A1AA] flex-shrink-0" />
                    <span className="text-sm text-[#D4D4D4] font-inter">{text}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <a
                  href="#download"
                  data-testid="shoutout-notify-btn"
                  className="inline-flex items-center gap-2 bg-[#a855f7] text-white font-bold text-sm uppercase tracking-wider rounded-full px-6 py-3 hover:bg-white hover:text-black transition-all duration-150"
                >
                  Notify Me on Launch
                  <ChevronRight size={15} />
                </a>
                <p className="text-[10px] text-[#A1A1AA] font-inter">Coming Soon After Launch</p>
              </div>
            </div>

            {/* Right: Creator earning card mockup */}
            <div className="lg:border-l border-white/6 p-8 lg:p-12 flex items-center justify-center bg-white/[0.01]">
              <div className="w-full max-w-xs space-y-4">
                <p className="text-[10px] text-[#A1A1AA] uppercase tracking-widest font-bold mb-5">Creator Earnings Preview</p>

                {/* Earning card */}
                {[
                  { name: "RohitFanatic", type: "Match Prediction", amount: "₹299", avatar: "R", color: "#3B9EFF" },
                  { name: "GoalKingFC", type: "Birthday Shoutout", amount: "₹499", avatar: "G", color: "#007AFF" },
                  { name: "CricketVault", type: "Fan Dedication", amount: "₹199", avatar: "C", color: "#f59e0b" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/[0.04] border border-white/6 rounded-xl p-3.5">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-outfit font-black text-sm"
                      style={{ background: `${item.color}20`, color: item.color, border: `1px solid ${item.color}30` }}>
                      {item.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-xs font-semibold">{item.name}</div>
                      <div className="text-[#A1A1AA] text-[10px]">{item.type}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#a855f7] font-outfit font-black text-sm">{item.amount}</div>
                      <div className="text-[#A1A1AA] text-[9px] uppercase tracking-wider">received</div>
                    </div>
                  </div>
                ))}

                <div className="bg-[#a855f7]/8 border border-[#a855f7]/20 rounded-xl p-4 text-center mt-2">
                  <p className="text-[#a855f7] font-outfit font-black text-xl mb-0.5">₹2,400+</p>
                  <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider">Total earned today</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
